import { useEffect, useRef, useState } from 'react';

// T270(6) — a multi-select facet dropdown, with the Escape/outside-click
// dismissal it needs. The hook travels with it: nothing else uses it.
// T248 — Escape closes a popover and gives the trigger its focus back.
//
// Both dropdowns could be opened from the keyboard and then only be closed with
// the mouse: Escape did nothing, and clicking the invisible backdrop dropped
// focus to <body>, so a keyboard user lost their place in the toolbar. The
// pattern is lifted verbatim from KebabMenu, which already had it right.
export function useDismissOnEscape(open, close, triggerRef) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
      triggerRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close, triggerRef]);
}

export default function FilterDropdown({ label, options, selected, onToggle, counts }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  useDismissOnEscape(open, () => setOpen(false), triggerRef);
  // A long option list is not scannable: Source can hold every adapter the last
  // scan queried. Above a dozen entries, let the operator type instead of
  // scrolling past fifty names that matched nothing.
  const [query, setQuery] = useState('');
  const count = selected.size;
  if (options.length === 0) return null;
  const needle = query.trim().toLowerCase();
  const shown = needle ? options.filter((o) => o.toLowerCase().includes(needle)) : options;
  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={
          'rounded-md border px-3 py-1.5 text-xs transition ' +
          (count > 0
            ? 'border-brand-600 bg-brand-600/15 text-brand-500'
            : 'border-slate-700 text-slate-300 hover:border-brand-600 hover:text-brand-400')
        }
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {label}{count > 0 ? ` · ${count}` : ''} ▾
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={() => {
              setOpen(false);
              setQuery('');
            }}
            aria-hidden="true"
          />
          <div className="absolute right-0 z-40 mt-1 min-w-[220px] rounded-md border border-slate-700 bg-slate-900 p-1 shadow-lg">
            {options.length > 12 && (
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Filter ${label.toLowerCase()}…`}
                aria-label={`Filter ${label} options`}
                className="mb-1 w-full rounded border border-slate-700 bg-slate-950/40 px-2 py-1 text-xs text-slate-100 focus:border-brand-500 focus:outline-none"
              />
            )}
            <div role="listbox" className="max-h-96 overflow-y-auto">
            {shown.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-500">No match</div>}
            {shown.map((opt) => {
              const on = selected.has(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => onToggle(opt)}
                  role="option"
                  aria-selected={on}
                  className={
                    'flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs transition ' +
                    (on ? 'bg-brand-600/15 text-brand-500' : 'text-slate-300 hover:bg-slate-800')
                  }
                >
                  <span className="truncate">{opt}</span>
                  <span className="ml-2 shrink-0 tabular-nums text-slate-500">
                    {on ? '✓' : (counts?.[opt] ?? '')}
                  </span>
                </button>
              );
            })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
